import { useState, useCallback, useRef } from 'react';
import { CanvasObject, DragState, ResizeState, SelectionBox } from '../types';
import { getCanvasCoordinates, createObject } from '../utils';
import { generateId } from '../constants';

export const useCanvasInteractions = (
    objects: CanvasObject[],
    setObjects: React.Dispatch<React.SetStateAction<CanvasObject[]>>,
    selectedObjects: string[],
    setSelectedObjects: React.Dispatch<React.SetStateAction<string[]>>,
    canvasRef: React.RefObject<HTMLDivElement>
) => {
    const [dragState, setDragState] = useState<DragState>({ isDragging: false, startX: 0, startY: 0 });
    const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
    const [isResizing, setIsResizing] = useState<ResizeState | null>(null);
    const [isDragOperation, setIsDragOperation] = useState(false);
    const rafRef = useRef<number | null>(null);

    const handleCanvasClick = useCallback((e: React.MouseEvent, selectedTool: string, onImageToolClick: (x: number, y: number) => void, setSelectedTool: (tool: string) => void) => {
        // Prevent click handling if we just finished a drag operation
        if (isDragOperation) {
            setIsDragOperation(false);
            return;
        }

        const { x, y } = getCanvasCoordinates(e, canvasRef);
        const canvasWidth = canvasRef.current?.offsetWidth || 0;
        const canvasHeight = canvasRef.current?.offsetHeight || 0;

        if (selectedTool === 'image') {
            // Constrain image position
            const imageWidth = 150;
            const imageHeight = 150;
            const constrainedX = Math.max(0, Math.min(x - 75, canvasWidth - imageWidth));
            const constrainedY = Math.max(0, Math.min(y - 75, canvasHeight - imageHeight));
            onImageToolClick(constrainedX, constrainedY);
        } else if (selectedTool !== 'select') {
            // Constrain object position
            const objectWidth = 100;
            const objectHeight = 50;
            const constrainedX = Math.max(0, Math.min(x - 50, canvasWidth - objectWidth));
            const constrainedY = Math.max(0, Math.min(y - 25, canvasHeight - objectHeight));

            const newObject = createObject(selectedTool, constrainedX, constrainedY);
            setObjects(prev => [...prev, newObject]);
            setSelectedObjects([newObject.id]);
            // Automatically switch back to select tool after placing an object
            setSelectedTool('select');
        } else {
            // Find all objects at click position, then select the topmost one (last in array)
            const clickedObjects = objects.filter(obj =>
                x >= obj.position.x && x <= obj.position.x + obj.size.width &&
                y >= obj.position.y && y <= obj.position.y + obj.size.height
            );

            // Get the topmost object (last in the filtered array)
            const clickedObject = clickedObjects.length > 0 ? clickedObjects[clickedObjects.length - 1] : null;

            if (clickedObject) {
                if (e.ctrlKey || e.metaKey) {
                    setSelectedObjects(prev =>
                        prev.includes(clickedObject.id)
                            ? prev.filter(id => id !== clickedObject.id)
                            : [...prev, clickedObject.id]
                    );
                } else {
                    setSelectedObjects([clickedObject.id]);
                }
            } else {
                setSelectedObjects([]);
            }
        }
    }, [objects, setObjects, setSelectedObjects, canvasRef, isDragOperation]);

    const handleMouseDown = useCallback((e: React.MouseEvent, selectedTool: string) => {
        const { x, y } = getCanvasCoordinates(e, canvasRef);

        if (selectedTool === 'select') {
            // Check if clicking on an object
            const clickedObject = objects.find(obj =>
                x >= obj.position.x && x <= obj.position.x + obj.size.width &&
                y >= obj.position.y && y <= obj.position.y + obj.size.height
            );

            if (!clickedObject) {
                // Start selection box when clicking on empty space
                setSelectionBox({ startX: x, startY: y, endX: x, endY: y });
                setDragState({ isDragging: true, startX: x, startY: y });
                setIsDragOperation(false); // Reset drag operation flag
            }
        }
    }, [objects, canvasRef]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        const { x, y } = getCanvasCoordinates(e, canvasRef);

        if (dragState.isDragging && selectionBox) {
            setSelectionBox(prev => prev ? { ...prev, endX: x, endY: y } : null);
            setIsDragOperation(true); // Mark as drag operation
        }

        if (isResizing) {
            // Cancel any pending animation frame
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }

            // Use requestAnimationFrame to batch updates
            rafRef.current = requestAnimationFrame(() => {
                const deltaX = x - isResizing.startX;
                const deltaY = y - isResizing.startY;

                setObjects(prev => prev.map(obj => {
                    if (obj.id === isResizing.objectId) {
                        let newWidth = isResizing.startWidth;
                        let newHeight = isResizing.startHeight;

                        if (isResizing.handle.includes('e')) newWidth += deltaX;
                        if (isResizing.handle.includes('s')) newHeight += deltaY;

                        return {
                            ...obj,
                            size: {
                                width: Math.max(10, newWidth),
                                height: Math.max(10, newHeight)
                            }
                        };
                    }
                    return obj;
                }));
            });
        }
    }, [dragState.isDragging, selectionBox, isResizing, setObjects, canvasRef]);

    const handleMouseUp = useCallback(() => {
        if (selectionBox) {
            const { startX, startY, endX, endY } = selectionBox;
            const minX = Math.min(startX, endX);
            const maxX = Math.max(startX, endX);
            const minY = Math.min(startY, endY);
            const maxY = Math.max(startY, endY);

            // Only select if the selection box has some size (not just a click)
            const hasSize = Math.abs(endX - startX) > 5 || Math.abs(endY - startY) > 5;

            if (hasSize) {
                // Select objects that intersect with the selection box
                const selectedIds = objects
                    .filter(obj => {
                        const objRight = obj.position.x + obj.size.width;
                        const objBottom = obj.position.y + obj.size.height;

                        // Check if object intersects with selection box
                        return !(obj.position.x > maxX || objRight < minX || obj.position.y > maxY || objBottom < minY);
                    })
                    .map(obj => obj.id);

                setSelectedObjects(selectedIds);
            }

            setSelectionBox(null);
        }

        setDragState({ isDragging: false, startX: 0, startY: 0 });
        setIsResizing(null);
    }, [selectionBox, objects, setSelectedObjects, dragState, isDragOperation]);

    const handleObjectMouseDown = useCallback((e: React.MouseEvent, objectId: string) => {
        e.stopPropagation();
        const { x, y } = getCanvasCoordinates(e, canvasRef);

        // Mark as drag operation immediately to prevent canvas click from clearing selection
        setIsDragOperation(true);

        if (!selectedObjects.includes(objectId)) {
            setSelectedObjects([objectId]);
        }

        setDragState({ isDragging: true, startX: x, startY: y, objectId });
    }, [selectedObjects, setSelectedObjects, canvasRef]);

    const handleObjectMouseMove = useCallback((e: React.MouseEvent) => {
        if (dragState.isDragging && dragState.objectId) {
            const { x, y } = getCanvasCoordinates(e, canvasRef);
            const deltaX = x - dragState.startX;
            const deltaY = y - dragState.startY;

            // Only mark as drag operation if mouse moved more than 3 pixels
            if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
                setIsDragOperation(true);
            }

            // Cancel any pending animation frame
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }

            // Use requestAnimationFrame to batch updates
            rafRef.current = requestAnimationFrame(() => {
                const { x: currentX, y: currentY } = getCanvasCoordinates(e, canvasRef);
                const deltaX = currentX - dragState.startX;
                const deltaY = currentY - dragState.startY;

                // Get canvas dimensions
                const canvasWidth = canvasRef.current?.offsetWidth || 0;
                const canvasHeight = canvasRef.current?.offsetHeight || 0;

                setObjects(prev => prev.map(obj => {
                    if (selectedObjects.includes(obj.id)) {
                        // Calculate new position
                        let newX = obj.position.x + deltaX;
                        let newY = obj.position.y + deltaY;

                        // Constrain regular objects within canvas bounds
                        newX = Math.max(0, Math.min(newX, canvasWidth - obj.size.width));
                        newY = Math.max(0, Math.min(newY, canvasHeight - obj.size.height));

                        return {
                            ...obj,
                            position: {
                                x: newX,
                                y: newY
                            }
                        };
                    }
                    return obj;
                }));

                setDragState(prev => ({ ...prev, startX: currentX, startY: currentY }));
            });
        }
    }, [dragState, selectedObjects, setObjects, canvasRef]);

    const handleResizeStart = useCallback((e: React.MouseEvent, objectId: string, handle: string) => {
        e.stopPropagation();
        const { x, y } = getCanvasCoordinates(e, canvasRef);
        const obj = objects.find(o => o.id === objectId);

        if (obj) {
            setIsResizing({
                objectId,
                handle,
                startX: x,
                startY: y,
                startWidth: obj.size.width,
                startHeight: obj.size.height,
            });
        }
    }, [objects, canvasRef]);

    const duplicateSelectedObjects = useCallback(() => {
        const newObjects = objects
            .filter(obj => selectedObjects.includes(obj.id))
            .map(obj => ({
                ...obj,
                id: generateId(),
                position: {
                    x: obj.position.x + 20,
                    y: obj.position.y + 20,
                }
            }));

        setObjects(prev => [...prev, ...newObjects]);
        setSelectedObjects(newObjects.map(obj => obj.id));
    }, [objects, selectedObjects, setObjects, setSelectedObjects]);

    const deleteSelectedObjects = useCallback(() => {
        setObjects(prev => prev.filter(obj => !selectedObjects.includes(obj.id)));
        setSelectedObjects([]);
    }, [selectedObjects, setObjects, setSelectedObjects]);

    return {
        dragState,
        selectionBox,
        isResizing,
        handleCanvasClick,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleObjectMouseDown,
        handleObjectMouseMove,
        handleResizeStart,
        duplicateSelectedObjects,
        deleteSelectedObjects,
    };
};